import * as Yup from 'yup';
import { App } from '../../App';
import { AppRegistrationSection } from '../../AppRegistrationSection';

import { ButtonActor, InputActor, Spacer, Text, Title } from 'components';
import { Frame } from 'components/organisms';
import { frameConfig } from '../../AppConfig';
import { getScreenTranslation } from '../../utils/translations';
import { mobilePhoneNumberValid } from '../../utils/regularExpressions';
import { routes } from 'routes';
import { useCallback, useState } from 'react';
import { useErrorManager, useFetched } from 'InternalLibrary';
import { useFormik } from 'formik';
import { useGetClient, useGetClientPhone } from '../../api/atoms';
import { useNavigate } from 'react-router-dom';

import { usePhoneVerificationScreenLogic } from '../PhoneVerification/usePhoneVerificationScreenLogic';
import { useTranslation } from 'react-i18next';
import layout from 'scss/layout.module.scss';
import styles from './PhoneNumberChange.module.scss';

export default function PhoneVerificationChangePhone() {
  const i18Next = useTranslation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const { setError } = useErrorManager();
  const { updateClientPhone } = usePhoneVerificationScreenLogic();
  const { fetch: fetchGetPhone } = App.useApi('client/phone', 'GET');

  const apiGetClient = useFetched(useGetClient());
  const apiGetClientPhone = useFetched(useGetClientPhone());

  const validationSchema = Yup.object().shape({
    phoneNumber: Yup.string()
      .required(i18Next.t('validation:required_field'))
      .test('wrong_format', i18Next.t('validation:wrong_format'), (value) =>
        mobilePhoneNumberValid(value ?? ''),
      ),
  });

  const onSubmit = useCallback(
    async (data: { phoneNumber: string }) => {
      formik.setStatus({ errors: {} });
      if (data.phoneNumber === apiGetClientPhone.data?.mobilePhone) {
        setError('same_phone');
      } else {
        try {
          setLoading(true);
          await updateClientPhone(data.phoneNumber);

          await fetchGetPhone();

          //allow to send sms with new phone number
          if (sessionStorage.getItem('lastSMSSent')) {
            localStorage.removeItem('lastSMSSent');
          }

          navigate(routes.FirstLoan_PhoneVerification);
        } catch (error) {
          if (
            error.fieldErrors?.[0]?.messageTemplate ===
              'already_exists_in_db' &&
            error.fieldErrors?.[0]?.property === 'mobilePhone'
          ) {
            setError('phone_already_exists');
          }
        } finally {
          setLoading(false);
        }
      }
    },
    [apiGetClient, apiGetClientPhone, updateClientPhone, navigate, setError],
  );

  const formik = useFormik({
    initialValues: {
      phoneNumber: '',
    },
    validationSchema,
    onSubmit,
  });

  const content = getScreenTranslation('phoneChange', i18Next);

  return (
    <Frame {...frameConfig} sectionID={AppRegistrationSection.Identification}>
      <div className={layout['width-sm']}>
        <Title text={content.heading} />
        <Spacer marginTop="23px" marginBottom="15px">
          <Text
            text={content.subheading}
            level={2}
            kind="p"
            className={styles.subheading}
            dataTest="phoneNumberChange-subheading"
          />
        </Spacer>

        <form onSubmit={formik.handleSubmit}>
          <InputActor
            formik={formik}
            name="phoneNumber"
            label={content.mobile_phone}
            sanitizer={(value: string) => value.trim().replace(/\D/g, '')}
            analytics={{
              dontTrackContent: true,
            }}
          />

          <ButtonActor
            buttonProps={{
              className: styles['continue-button'],
              dataTest: 'phoneNumberChange-continue-btn',
              type: 'submit',
              loading,
            }}
            formik={formik}
          >
            {content.continue_button}
          </ButtonActor>
        </form>
      </div>
    </Frame>
  );
}
